import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useUserStore } from '@/stores/userStore'

export const usePermissionStore = defineStore('permissions', () => {
  const userStore = useUserStore();
  const error = ref(null)

  // Computed properties para verificar permissões específicas
  const hasPermission = computed(() => {
    return (permission) => {
      const hasPerm = userStore.permissions.includes(permission)
      // console.log(`Verificando permissão: ${permission}, Tem permissão: ${hasPerm}, Permissões disponíveis:`, userStore.permissions)
      return hasPerm
    }
  })

  // Permissões de acesso aos módulos
  const canAccessHome = computed(() => hasPermission.value('can_access_home'))
  const canAccessSchedule = computed(() => hasPermission.value('can_access_schedule'))
  const canAccessPatients = computed(() => hasPermission.value('can_access_patients'))
  const canAccessAtendimento = computed(() => hasPermission.value('can_access_atendimento'))
  const canAccessMedicalReports = computed(() => hasPermission.value('can_access_medicalreports'))
  const canAccessExamRelease = computed(() => hasPermission.value('can_access_exam_release'))
  const canAccessEditExam = computed(() => hasPermission.value('can_access_edit_exam'))
  const canAccessExamDelivery = computed(() => hasPermission.value('can_access_exam_delivery'))
  const canAccessRecallQueue = computed(() => hasPermission.value('can_access_recall_queue'))
  const canAccessReports = computed(() => hasPermission.value('can_access_reports'))
  const canAccessFaturamento = computed(() => hasPermission.value('can_access_faturamento'))
  const canAccessEstoque = computed(() => hasPermission.value('Estoque.can_access_estoque'))
  const canAccessConfig = computed(() => hasPermission.value('can_access_config'))
  const canAccessMonitor = computed(() => hasPermission.value('can_access_monitor'))
  const canAccessViewer = computed(() => hasPermission.value('can_access_viewer'))

  // Permissões de configuração
  const canManageUsers = computed(() => hasPermission.value('can_manage_users'))
  const canManageGroups = computed(() => hasPermission.value('can_manage_groups'))
  const canManagePermissions = computed(() => hasPermission.value('can_manage_permissions'))
  const canManageClinicSettings = computed(() => hasPermission.value('can_manage_clinic_settings'))
  const canManageScheduleSettings = computed(() => hasPermission.value('can_manage_schedule_settings'))
  const canManageRoomSettings = computed(() => hasPermission.value('can_manage_room_settings'))
  const canManageServiceSettings = computed(() => hasPermission.value('can_manage_service_settings'))
  const canManageInsuranceSettings = computed(() => hasPermission.value('can_manage_insurance_settings'))
  const canManageDoctorSettings = computed(() => hasPermission.value('can_manage_doctor_settings'))
  const canManageAnamnesisSettings = computed(() => hasPermission.value('can_manage_anamnesis_settings'))
  const canManageWebhookSettings = computed(() => hasPermission.value('can_manage_webhook_settings'))
  const canManageNotificationSettings = computed(() => hasPermission.value('can_manage_notification_settings'))
  const canViewAudit = computed(() => hasPermission.value('can_view_audit'))
  const canManageMonitorSettings = computed(() => hasPermission.value('can_manage_monitor_settings'))

  // Função para limpar as permissões (logout)
  const clearPermissions = () => {
    error.value = null
  }

  // Função para verificar se o usuário tem permissão para acessar uma rota
  const canAccessRoute = (routeName) => {
    const routePermissions = {
      'HomePage': 'can_access_home',
      'SchedulePage': 'can_access_schedule',
      'PatientsPage': 'can_access_patients',
      'AtendimentoPage': 'can_access_atendimento',
      'LaudoPage': 'can_access_medicalreports',
      'ExamRelease': 'can_access_exam_release',
      'EditExam': 'can_access_edit_exam',
      'ExamDelivery': 'can_access_exam_delivery',
      'RecallQueue': 'can_access_recall_queue',
      'ReportsPage': 'can_access_reports',
      'ConfigScreen': 'can_access_config',
      'MonitorPage': 'can_access_monitor',
      'Viewer': 'can_access_viewer',
      'PacsViewer': 'can_access_viewer',
      'ShareViewer': 'can_access_viewer',
    }

    const requiredPermission = routePermissions[routeName]
    return !requiredPermission || hasPermission.value(requiredPermission)
  }

  return {
    // State
    error,

    // Computed
    hasPermission,
    canAccessHome,
    canAccessSchedule,
    canAccessPatients,
    canAccessAtendimento,
    canAccessMedicalReports,
    canAccessExamRelease,
    canAccessEditExam,
    canAccessExamDelivery,
    canAccessRecallQueue,
    canAccessReports,
    canAccessFaturamento,
    canAccessEstoque,
    canAccessConfig,
    canAccessMonitor,
    canAccessViewer,
    canManageUsers,
    canManageGroups,
    canManagePermissions,
    canManageClinicSettings,
    canManageScheduleSettings,
    canManageRoomSettings,
    canManageServiceSettings,
    canManageInsuranceSettings,
    canManageDoctorSettings,
    canManageAnamnesisSettings,
    canManageWebhookSettings,
    canManageNotificationSettings,
    canViewAudit,
    canManageMonitorSettings,

    // Actions
    clearPermissions,
    canAccessRoute,
  }
}) 